



import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
} from "react-bootstrap";
import { Pie, Line, Bar } from "react-chartjs-2"; // Import Pie and Line from react-chartjs-2
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js"; // Import necessary Chart.js components
import { FaCalendarAlt, FaFilter, FaBackspace } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Navbar from "../../../Shared/AdminNavbar/Navbar";
import "./Dashboard.css";
import "./../../../Layout/Collapse/Collapse.css";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [collapsed, setCollapsed] = React.useState(false);
  const [fromDate, setFromDate] = useState(new Date("2024-04-01"));
  const [toDate, setToDate] = useState(new Date("2025-06-29"));

  const [dealOwner, setDealOwner] = useState("All");
  const [dealStage, setDealStage] = useState("All");
  const [pipeline, setPipeline] = useState("All");
  const [dealLabel, setDealLabel] = useState("All");

  // Pie Chart Data for Sales Pipeline
  const pieChartData = {
    labels: ["Negotiation", "Lead in", "Proposal", "Interview", "Contact Made", "Closed Lost"],
    datasets: [
      {
        data: [5.12, 27.11, 9.42, 14.79, 18.43, 21.53],
        backgroundColor: ["#4caf50", "#2196f3", "#ff9800", "#e91e63", "#9c27b0", "#f44336"],
        borderWidth: 1,
      },
    ],
  };

  // Pie Chart Data for Deal Loss Reasons
  const dealLossData = {
    labels: ["Lack of urgency", "Feature limitations", "Better alternative", "Price too high", "Budget constraints"],
    datasets: [
      {
        data: [13.05, 33.78, 15.16, 17.27, 20.73],
        backgroundColor: ["#ff5722", "#3f51b5", "#009688", "#ffeb3b", "#607d8b"],
        borderWidth: 1,
      },
    ],
  };

  // Chart.js Options for Pie Charts
  const pieChartOptions = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 12 },
        },
      },
    },
    maintainAspectRatio: false,
  };

  // Summary Data
  const summaryData = [
    { title: "Total Sales", value: "10.7M", color: "#6B48FF" },
    { title: "Win Rate", value: "15.48%", color: "#1976D2" },
    { title: "Close Rate", value: "13.41%", color: "#1976D2" },
    { title: "Avg days to close", value: "60.58", color: "#2E7D32" },
    { title: "Pipeline Value", value: "144.0M", color: "#6B48FF" },
    { title: "Open Deals", value: "2.9K", color: "#1976D2" },
    { title: "Weighted Value", value: "64.6M", color: "#1976D2" },
    { title: "Avg open deal age", value: "40.86", color: "#2E7D32" },
  ];

  // Line Chart Data
  const lineChartData = {
    labels: ["Sep 2024", "Nov 2024", "Jan 2025", "Mar 2025", "Jul 2025", "Sep 2025"],
    datasets: [
      {
        label: "Closed value",
        data: [1000, 800, 1200, 900, 1100, 1300],
        borderColor: "#1976D2",
        backgroundColor: "rgba(25, 118, 210, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Won deals",
        data: [800, 640, 960, 720, 880, 1040], // 80% of Closed value
        borderColor: "#2E7D32",
        backgroundColor: "rgba(46, 125, 50, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Line Chart Data for Projection
  const projectionChartData = {
    labels: ["Sep 2024", "Nov 2024", "Jan 2025", "Mar 2025", "Jul 2025", "Sep 2025"],
    datasets: [
      {
        label: "Projected value",
        data: [1200, 960, 1440, 1080, 1320, 1560], // 120% of original
        borderColor: "#6B48FF",
        backgroundColor: "rgba(107, 72, 255, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Deals due",
        data: [900, 720, 1080, 810, 990, 1170], // 90% of original
        borderColor: "#FF9800",
        backgroundColor: "rgba(255, 152, 0, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Line Chart Options
  const lineChartOptions = {
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 12 } },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 12 } },
      },
      y: {
        ticks: { font: { size: 12 } },
        beginAtZero: true,
      },
    },
    maintainAspectRatio: false,
  };

  // Funnel Data
  const funnelData = [
    { value: 2000, label: "Accounts", color: "funnel-red" },
    { value: 1600, label: "Accounts with deals", color: "funnel-orange" },
    { value: 1440, label: "Accounts with deals > $10000", color: "funnel-green" },
    { value: 1080, label: "Accounts with won deals", color: "funnel-blue" },
  ];

  // Deals Won vs Sales Rep
  const salesData = {
    labels: [
      "Patricia B",
      "Arthi Agrawal",
      "James Smith",
      "Tim Simpson",
      "Jeff Smith",
      "Einhard Klein",
      "Nathan Brooks",
      "Manish Patel",
    ],
    datasets: [
      {
        label: "Deals Won",
        data: [236500, 145000, 127500, 277000, 175000, 135000, 275000, 170000],
        backgroundColor: [
          "#f56954",
          "#f39c12",
          "#00c0ef",
          "#3c8dbc",
          "#00a65a",
          "#605ca8",
          "#39CCCC",
          "#ff851b",
        ],
        borderRadius: 8,
      },
    ],
  };

  const salesOptions = {
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `$ ${ctx.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: (val) => `$${val / 1000}k` },
      },
      y: {
        ticks: { color: "#333", font: { size: 14 } },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="CollapseContainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`Collapse ${collapsed ? "collapsed" : ""}`}>
        <Container fluid className="py-3" style={{ background: "#f5f9ff" }}>
          <Card className="p-3" style={{ borderRadius: "20px" }}>
            <Row>
              {/* LEFT SIDE */}
              <Col lg={8}>
                {/* Summary Cards */}
                <Row className="g-1 mb-3">
                  {summaryData.map((item, index) => (
                    <Col xs={6} md={3} key={index}>
                      <Card
                        style={{
                          background: item.color,
                          color: "#fff",
                          borderRadius: "10px",
                        }}
                      >
                        <Card.Body className="text-center">
                          <h6>{item.title}</h6>
                          <h5 className="fw-bold">{item.value}</h5>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Line Charts */}
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Won deals (last 12 months)</h6>
                    <div style={{ height: "300px", overflowX: "auto" }}>
                      <Line data={lineChartData} options={lineChartOptions} />
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Body>
                    <h6>Deals projection (future 12 months)</h6>
                    <div style={{ height: "300px", overflowX: "auto" }}>
                      <Line data={projectionChartData} options={lineChartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* RIGHT SIDE FILTERS */}
              <Col lg={4}>
                <Card style={{ height: "600px", width: "100%" }}>
                  <Card.Body>
                    <h6 className="fw-bold d-flex align-items-center mb-3">
                      <FaFilter className="me-2 text-primary" /> Filters
                    </h6>

                    {/* Dates */}
                    <h6 className="fw-bold">Report date</h6>
                    <Row className="mb-3">
                      <Col>
                        <InputGroup>
                          <DatePicker
                            selected={fromDate}
                            onChange={(date) => setFromDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            placeholderText="From Date"
                          />
                          <Button variant="light" onClick={() => setFromDate(null)}>
                            <FaBackspace />
                          </Button>
                        </InputGroup>
                      </Col>
                      <Col>
                        <InputGroup>
                          <DatePicker
                            selected={toDate}
                            onChange={(date) => setToDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            placeholderText="To Date"
                          />
                          <Button variant="light" onClick={() => setToDate(null)}>
                            <FaBackspace />
                          </Button>
                        </InputGroup>
                      </Col>
                    </Row>

                    {/* Dropdowns */}
                    {[
                      {
                        label: "Deal Owner",
                        value: dealOwner,
                        setValue: setDealOwner,
                        options: ["All", "John", "Sarah", "Michael"],
                      },
                      {
                        label: "Deal Stage",
                        value: dealStage,
                        setValue: setDealStage,
                        options: [
                          "All",
                          "Prospect",
                          "Qualification",
                          "Proposal",
                          "Negotiation",
                          "Closed Won",
                          "Closed Lost",
                        ],
                      },
                      {
                        label: "Pipeline",
                        value: pipeline,
                        setValue: setPipeline,
                        options: ["All", "Standard", "Enterprise", "Partner"],
                      },
                      {
                        label: "Deal Label",
                        value: dealLabel,
                        setValue: setDealLabel,
                        options: ["All", "Hot", "Warm", "Cold", "New"],
                      },
                    ].map((filter, idx) => (
                      <Form.Group className="mb-3" key={idx}>
                        <InputGroup>
                          <Form.Select
                            value={filter.value}
                            onChange={(e) => filter.setValue(e.target.value)}
                          >
                            {filter.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Form.Select>
                          <Button variant="light" onClick={() => filter.setValue("All")}>
                            <FaBackspace />
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Pie Charts */}
            <Row className="mt-3">
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <h6 className="fw-bold">Sales pipeline</h6>
                    <div style={{ height: "220px" }}>
                      <Pie data={pieChartData} options={pieChartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <h6 className="fw-bold">Deal loss reasons</h6>
                    <div style={{ height: "220px" }}>
                      <Pie data={dealLossData} options={pieChartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Container className="text-center my-4">
              <Row className="mt-3">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h5 className="fw-bold">ACCOUNTS ANALYTICS</h5>
                      <div className="funnel-container mt-4">
                        {funnelData.map((step, index) => (
                          <div key={index} className={`funnel-step ${step.color}`}>
                            <span>{step.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <span className="legend-box funnel-red me-2"></span> Accounts
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <span className="legend-box funnel-orange me-2"></span> Accounts with deals
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <span className="legend-box funnel-green me-2"></span> Accounts with deals greater than $10000
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="legend-box funnel-blue me-2"></span> Accounts with won deals
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h5 className="fw-bold mt-5 mb-3">Deals Won vs Sales Rep</h5>
                      <div style={{ height: "300px" }}>
                        <Bar data={salesData} options={salesOptions} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default Dashboard;